<template>
  <div class="support-image-attachment">
    <div v-if="file" class="support-image-preview">
      <image-preview v-if="previewUrl" :images="[previewUrl]">
        <img :src="previewUrl" alt="Attached screenshot preview" />
      </image-preview>
      <button
        type="button"
        class="support-image-remove"
        :disabled="disabled"
        aria-label="Remove image"
        @click="$emit('remove')"
      >
        <a-icon type="close" />
      </button>
    </div>

    <div class="support-image-picker">
      <a-upload :accept="accept" :before-upload="beforeUpload" :disabled="disabled" :show-upload-list="false">
        <button type="button" class="support-image-add" :disabled="disabled" aria-label="Attach image">
          <a-icon type="plus" />
        </button>
      </a-upload>
    </div>
  </div>
</template>

<script>
import { Icon, notification, Upload } from 'ant-design-vue'
import ImageViewer from '@/components/entities/viewer/image-viewer.vue'
import { SUPPORT_IMAGE_ACCEPT, SUPPORT_IMAGE_MAX_SIZE, SUPPORT_IMAGE_TYPES } from '@/store/modules/agent/constants'

export default {
  components: {
    'a-icon': Icon,
    'a-upload': Upload,
    'image-preview': ImageViewer
  },
  props: {
    file: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      accept: SUPPORT_IMAGE_ACCEPT,
      previewUrl: ''
    }
  },
  watch: {
    file: {
      handler(file) {
        this.updatePreview(file)
      },
      immediate: true
    }
  },
  mounted() {
    document.addEventListener('paste', this.handlePaste)
  },
  beforeDestroy() {
    document.removeEventListener('paste', this.handlePaste)
    this.revokePreview()
  },
  methods: {
    beforeUpload(file) {
      this.selectFile(file)
      return false
    },

    selectFile(file) {
      if (this.disabled || !file) return false

      if (!SUPPORT_IMAGE_TYPES.includes(file.type)) {
        this.showSelectionError('Choose a JPEG, PNG, or WebP image.')
        return false
      }

      if (file.size > SUPPORT_IMAGE_MAX_SIZE) {
        this.showSelectionError('Choose an image up to 10 MB.')
        return false
      }

      this.$emit('select', file)
      return true
    },

    handlePaste(event) {
      if (!this.active || this.disabled) return

      const imageItem = Array.from(event.clipboardData?.items ?? []).find(
        item => item.kind === 'file' && item.type.startsWith('image/')
      )
      if (!imageItem) return

      const file = imageItem.getAsFile()
      if (!file) return

      event.preventDefault()
      this.selectFile(file)
    },

    updatePreview(file) {
      this.revokePreview()
      if (file instanceof Blob) this.previewUrl = URL.createObjectURL(file)
    },

    revokePreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = ''
    },

    showSelectionError(description) {
      notification.error({
        message: 'Image not attached',
        description
      })
    }
  }
}
</script>

<style scoped>
.support-image-attachment {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.support-image-preview {
  position: relative;
  align-self: flex-start;
}

.support-image-preview img {
  display: block;
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: var(--border-radius);
  cursor: pointer;
}

.support-image-remove,
.support-image-add {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  cursor: pointer;
}

.support-image-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border: 1px solid var(--border-color-base);
  border-radius: 50%;
  background: var(--bg-body);
  color: var(--text-color);
  font-size: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.support-image-picker {
  display: flex;
}

.support-image-add {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  color: var(--text-color-secondary);
  font-size: 18px;
}

.support-image-remove:disabled,
.support-image-add:disabled {
  opacity: 0.5;
  cursor: default;
}

.support-image-add:not(:disabled):hover {
  background: var(--bg-row-hover);
  color: var(--text-color);
}
</style>
